/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Formula } from '@gh/builder-shared-data';
import {
  combineReducers,
  composeSetters,
  fieldValidationSetter,
  onChangeReducer,
  removeDirtyRow,
  setDirty,
  setEntityField,
  setNewDirtyType,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { flatMap, isEqual, uniq } from 'lodash';
import {
  getHpeItemTopIds,
  HpeCostCenter,
  hpeFormulaNeedPropagationSetter,
  HpeItem,
  hpeQuantityResetSetter,
  isHpeItemFieldAffectsPrice,
  validateHpeItemField,
} from '../../../resources/hpe-details.model';
import {
  HpeDetailsState,
  HpeDetailsStateEvent,
  HpeDetailsStateEventType,
  hpeItemTable,
  queryHpeItem,
} from '../hpe-details.state';
import { updateCostCenterItemCount, updateCostCenters } from './hpe-details-cc.reducers';
import { cuttingListReducer, rootCuttingListReducer } from './hpe-details-cutting-list.reducers';
import { selectionConsistencyReducer } from './hpe-details-selection.reducers';
import { addOrUpdateTops, getModifiedTops } from './hpe-details-top.reducers';

const setCostCenterItemField = composeSetters(
  setEntityField,
  hpeFormulaNeedPropagationSetter,
  hpeQuantityResetSetter,
  setUpdatedDirtyType,
  fieldValidationSetter(validateHpeItemField));

export const hpeDetailsCcItemReducer = onChangeReducer(
  combineReducers({
    hpItems: itemsReducer,
  }),
  costCenterItemOnChangeReducer,
  formulaItemOnChangeReducer,
  rootItemOnChangeReducer,
  toOnChangeReducer(selectionConsistencyReducer),
);

function itemsReducer(items: Table<HpeItem>, event: HpeDetailsStateEvent): Table<HpeItem> {
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterItemAddSet:
      return hpeItemTable.insertAll(items, event.itemSet.map(setNewDirtyType));
    case HpeDetailsStateEventType.CostCenterItemSetField:
      return hpeItemTable.mergeById(
        items,
        event.itemId,
        (row: HpeItem) => setCostCenterItemField(row, event.name, event.value));
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemRemove:
    case HpeDetailsStateEventType.CuttingListItemSetField:
    case HpeDetailsStateEventType.ResetFormulaNeedPropagation:
      return hpeItemTable.mergeById(
        items,
        event.sourceId,
        (row: HpeItem) => itemReducer(row, event));
    case HpeDetailsStateEventType.CostCenterItemRemove:
      return hpeItemTable.mergeById(items, event.itemId, removeDirtyRow);
    default:
      return items;
  }
}

function itemReducer(item: HpeItem, event: HpeDetailsStateEvent): HpeItem {
  switch (event.type) {
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemRemove:
    case HpeDetailsStateEventType.CuttingListItemSetField:
      return setCostCenterItemField(item, 'cuttingList', cuttingListReducer(item.cuttingList || [], event));
    case HpeDetailsStateEventType.ResetFormulaNeedPropagation:
      return setCostCenterItemField(item, 'isFormulaNeedPropagation', false);
    default:
      return item;
  }
}

const updateCcDirtyType = (data: Maybe<HpeCostCenter>, id: ID) => data ? setUpdatedDirtyType(data) : void 0;
const updateCountAndDirtyType = (state: HpeDetailsState) => (cc: HpeCostCenter) =>
  setUpdatedDirtyType(updateCostCenterItemCount(state, cc));

function costCenterItemOnChangeReducer(oldState: HpeDetailsState,
                                       newState: HpeDetailsState,
                                       event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterItemAddSet:
      return updateCostCenters(newState, [event.costCenterId], updateCountAndDirtyType(newState));
    case HpeDetailsStateEventType.CostCenterItemSetField:
      return updateCostCenters(newState, [getCostCenterItemId(newState, event.itemId)], updateCcDirtyType);
    case HpeDetailsStateEventType.CostCenterItemRemove:
      return updateCostCenters(
        newState,
        [getCostCenterItemId(oldState, event.itemId)],
        updateCountAndDirtyType(newState));
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemSetField:
    case HpeDetailsStateEventType.CuttingListItemRemove:
      return updateCostCenters(newState, [getCostCenterItemId(newState, event.sourceId)], updateCcDirtyType);
    default:
      return newState;
  }
}

function formulaItemOnChangeReducer(oldState: HpeDetailsState,
                                    newState: HpeDetailsState,
                                    event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterItemAddSet:
      return addOrUpdateTops(newState, uniq(flatMap(event.itemSet, getHpeItemTopIds)));
    case HpeDetailsStateEventType.CostCenterItemSetField:
      if (event.name === 'formula') {
        const formula: Formula = event.value;
        const item = <HpeItem>hpeItemTable.byId(newState.hpItems, event.itemId);
        if (formula.valid && !isEqual(formula.takeOffParams, item.topIds)) {
          return addOrUpdateTops(
            setEntityField(
              newState,
              'hpItems',
              hpeItemTable.update(newState.hpItems, { ...item, topIds: formula.takeOffParams })),
            getModifiedTops(getHpeItemTopIds(item), formula.takeOffParams));
        }
      }
      return newState;
    case HpeDetailsStateEventType.CostCenterItemRemove: {
      const item = <HpeItem>hpeItemTable.byId(oldState.hpItems, event.itemId);
      return addOrUpdateTops(newState, getHpeItemTopIds(item));
    }
    default:
      return newState;
  }
}

function rootItemOnChangeReducer(_: HpeDetailsState,
                                 state: HpeDetailsState,
                                 event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterItemAddSet:
    case HpeDetailsStateEventType.CostCenterItemRemove:
      return setRecalculationNeeded(setDirty(state));
    case HpeDetailsStateEventType.CostCenterItemSetField:
      if (isHpeItemFieldAffectsPrice(event.name)) {
        return setRecalculationNeeded(setDirty(state));
      } else {
        return setDirty(state);
      }
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemSetField:
    case HpeDetailsStateEventType.CuttingListItemRemove:
      return rootCuttingListReducer(state, event);
    default:
      return state;
  }
}

function getCostCenterItemId(state: HpeDetailsState, itemId: ID): ID {
  return queryHpeItem(state, itemId)!.costCenterId; // tslint:disable-line:no-non-null-assertion
}
